import { useMemo, useRef, useState } from "react";

interface AreaChartProps {
  data: { label: string; value: number }[];
  height?: number;
  valueSuffix?: string;
  color?: string;
  tooltip?: boolean;
}

export default function AreaChart({
  data,
  height = 260,
  valueSuffix = "",
  color = "#00AAB7",
  tooltip = true,
}: AreaChartProps) {
  const W = 1000;
  const padX = 12;
  const padTop = 24;
  const padBottom = 36;

  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const chart = useMemo(() => {
    const maxVal = Math.max(...data.map((d) => d.value), 1);
    const innerW = W - padX * 2;
    const innerH = height - padTop - padBottom;
    const stepX = data.length > 1 ? innerW / (data.length - 1) : innerW;
    const points = data.map((d, i) => ({
      x: padX + i * stepX,
      y: padTop + innerH - (d.value / maxVal) * innerH,
      label: d.label,
      value: d.value,
    }));
    const linePath = points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
      .join(" ");
    const baseY = padTop + innerH;
    const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${baseY.toFixed(1)} L ${points[0].x.toFixed(1)} ${baseY.toFixed(1)} Z`;
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round((maxVal * f) / 100) * 100);
    return { points, linePath, areaPath, yTicks, baseY, maxVal, innerH };
  }, [data, height]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!tooltip) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0) return;
    const x = ((e.clientX - rect.left) / rect.width) * W;
    let nearest = 0;
    let minDist = Infinity;
    chart.points.forEach((p, i) => {
      const d = Math.abs(p.x - x);
      if (d < minDist) {
        minDist = d;
        nearest = i;
      }
    });
    setHover(nearest);
  };

  const hoverPoint = hover !== null ? chart.points[hover] : null;

  // Tooltip box dimensions (in viewBox units)
  const tw = 132;
  const th = 50;
  let tx = hoverPoint ? hoverPoint.x - tw / 2 : 0;
  let ty = hoverPoint ? hoverPoint.y - th - 14 : 0;
  if (hoverPoint) {
    tx = Math.max(padX, Math.min(tx, W - padX - tw));
    if (ty < padTop) ty = hoverPoint.y + 16;
  }

  return (
    <div dir="ltr" className="w-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${height}`}
        className="h-auto w-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHover(null)}
        style={tooltip ? { cursor: "crosshair" } : undefined}
      >
        <defs>
          <linearGradient id="ghasla-area-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.12" />
            <stop offset="100%" stopColor={color} stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {chart.yTicks.map((t, i) => {
          const y = chart.baseY - (i / 4) * (chart.baseY - padTop);
          return (
            <g key={i}>
              <line x1={padX} y1={y} x2={W - padX} y2={y} stroke="#E4F5F5" strokeWidth="1" />
              <text x={W - padX - 4} y={y - 5} textAnchor="end" fontSize="11" fill="#8AB5B5">
                {t.toLocaleString("en-US")}
                {valueSuffix}
              </text>
            </g>
          );
        })}

        <path d={chart.areaPath} fill="url(#ghasla-area-fill)" />
        <path
          d={chart.linePath}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {chart.points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3.5" fill="#ffffff" stroke={color} strokeWidth="2" />
            <text x={p.x} y={height - 10} textAnchor="middle" fontSize="11" fill="#8AB5B5">
              {p.label}
            </text>
          </g>
        ))}

        {hoverPoint && (
          <g>
            <line
              x1={hoverPoint.x}
              y1={padTop}
              x2={hoverPoint.x}
              y2={chart.baseY}
              stroke={color}
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.5"
            />
            <circle cx={hoverPoint.x} cy={hoverPoint.y} r="6" fill="#ffffff" stroke={color} strokeWidth="2.5" />
            <rect x={tx} y={ty} width={tw} height={th} rx="8" fill="#007A85" />
            <text
              x={tx + tw / 2}
              y={ty + 19}
              textAnchor="middle"
              fontSize="11"
              fill="#ffffff"
              fontWeight="600"
            >
              {hoverPoint.label}
            </text>
            <text
              x={tx + tw / 2}
              y={ty + 37}
              textAnchor="middle"
              fontSize="13"
              fill="#33D1DA"
              fontWeight="700"
            >
              {hoverPoint.value.toLocaleString("en-US")} {valueSuffix}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}